import React, { useState, useEffect } from 'react';
import {
  Plus,
  RefreshCw,
  Trash2,
  Edit,
  ChevronDown,
  Rss,
  CheckCircle,
  Search,
  X,
  Loader2,
  ExternalLink,
  Calendar,
  User,
} from 'lucide-react';

import {
  feedsService,
  Feed,
  CreateFeedRequest,
} from '../services/feedsService';
import { apiService } from '../services/api';
import TierPageBanner from '../components/portfolio/TierPageBanner';

interface FeedStats {
  total: number;
  active: number;
  inactive: number;
  recentlyFetched: number;
  neverFetched: number;
}

const Feeds: React.FC = () => {
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [stats, setStats] = useState<FeedStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [editingFeed, setEditingFeed] = useState<Feed | null>(null);
  const [fetchingFeeds, setFetchingFeeds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [expandedFeeds, setExpandedFeeds] = useState<string[]>([]);
  const [feedArticles, setFeedArticles] = useState<{ [feedId: string]: any[] }>(
    {},
  );
  const [loadingArticles, setLoadingArticles] = useState<string[]>([]);
  const [fetchingAllActive, setFetchingAllActive] = useState(false);
  const [analyzingArticles, setAnalyzingArticles] = useState<string[]>([]);

  // Form state
  const [formData, setFormData] = useState<CreateFeedRequest>({
    name: '',
    url: '',
    description: '',
    isActive: true,
  });

  // Bulk form state
  const [bulkUrls, setBulkUrls] = useState('');

  useEffect(() => {
    loadFeeds();
  }, []);

  const loadFeeds = async () => {
    try {
      setLoading(true);
      setError(null);
      const [feedsData, statsData] = await Promise.all([
        feedsService.getFeeds(),
        feedsService.getFeedStats(),
      ]);
      setFeeds(feedsData);
      setStats(statsData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFeed = async () => {
    try {
      setError(null);
      await feedsService.createFeed(formData);
      setOpenDialog(false);
      setFormData({ name: '', url: '', description: '', isActive: true });
      loadFeeds();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUpdateFeed = async () => {
    if (!editingFeed) return;
    try {
      setError(null);
      await feedsService.updateFeed(editingFeed._id, formData);
      setOpenDialog(false);
      setEditingFeed(null);
      setFormData({ name: '', url: '', description: '', isActive: true });
      loadFeeds();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteFeed = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this feed?')) return;
    try {
      setError(null);
      await feedsService.deleteFeed(id);
      loadFeeds();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleFetchFeed = async (id: string) => {
    try {
      setFetchingFeeds((prev) => [...prev, id]);
      setError(null);
      await feedsService.fetchFeed(id);
      loadFeeds();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setFetchingFeeds((prev) => prev.filter((feedId) => feedId !== id));
    }
  };

  const handleFetchAllActive = async () => {
    try {
      setError(null);
      setFetchingAllActive(true);
      await feedsService.fetchAllActiveFeeds();
      loadFeeds();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setFetchingAllActive(false);
    }
  };

  const handleToggleStatus = async (feed: Feed) => {
    try {
      setError(null);
      await feedsService.toggleFeedStatus(feed._id, !feed.isActive);
      loadFeeds();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleBulkCreate = async () => {
    try {
      setError(null);
      const urls = bulkUrls
        .split('\n')
        .map((url) => url.trim())
        .filter(Boolean);
      if (urls.length === 0) {
        setError('Please enter at least one URL');
        return;
      }
      await feedsService.createBulkFeeds(urls);
      setOpenBulkDialog(false);
      setBulkUrls('');
      loadFeeds();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAnalyzeArticle = async (articleId: string) => {
    try {
      setAnalyzingArticles((prev) => [...prev, articleId]);
      setError(null);
      await apiService.analyzeArticle(articleId);
      // Reload the specific feed's articles to get updated analysis
      const feedId = feeds.find((feed) =>
        feedArticles[feed._id]?.some((article) => article._id === articleId),
      )?._id;
      if (feedId) {
        const result = await feedsService.getFeedArticles(feedId, {
          page: 1,
          limit: 50,
          sortBy: 'publishedAt',
          sortOrder: 'desc',
        });
        setFeedArticles((prev) => ({
          ...prev,
          [feedId]: result.articles || [],
        }));
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setAnalyzingArticles((prev) => prev.filter((id) => id !== articleId));
    }
  };

  const openEditDialog = (feed: Feed) => {
    setEditingFeed(feed);
    setFormData({
      name: feed.name,
      url: feed.url,
      description: feed.description || '',
      isActive: feed.isActive,
    });
    setOpenDialog(true);
  };

  const openCreateDialog = () => {
    setEditingFeed(null);
    setFormData({ name: '', url: '', description: '', isActive: true });
    setOpenDialog(true);
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  };

  const toggleFeedExpansion = async (feedId: string) => {
    const isCurrentlyExpanded = expandedFeeds.includes(feedId);

    if (isCurrentlyExpanded) {
      // Collapse
      setExpandedFeeds((prev) => prev.filter((id) => id !== feedId));
    } else {
      // Expand and load articles
      setExpandedFeeds((prev) => [...prev, feedId]);

      // Only load articles if we haven't loaded them before
      if (!feedArticles[feedId]) {
        setLoadingArticles((prev) => [...prev, feedId]);
        try {
          const result = await feedsService.getFeedArticles(feedId, {
            page: 1,
            limit: 50,
            sortBy: 'publishedAt',
            sortOrder: 'desc',
          });
          setFeedArticles((prev) => ({
            ...prev,
            [feedId]: result.articles || [],
          }));
        } catch (error) {
          console.error(`Failed to load articles for feed ${feedId}:`, error);
        } finally {
          setLoadingArticles((prev) => prev.filter((id) => id !== feedId));
        }
      }
    }
  };

  const formatFeedItem = (item: any) => {
    const title = item.title || 'No title';
    const description = item.description || item.summary || item.content || '';
    const link = item.url || item.link || '';
    const pubDate =
      item.publishedAt ||
      item.pubDate ||
      item.published ||
      item.updated ||
      item.date;
    const author = item.author || item.creator || '';

    // Clean HTML from description
    const cleanDescription = description.replace(/<[^>]*>/g, '').trim();

    return {
      title,
      description: cleanDescription,
      link,
      pubDate,
      author,
    };
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment?.toLowerCase()) {
      case 'bullish':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'bearish':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'neutral':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment?.toLowerCase()) {
      case 'bullish':
        return '📈';
      case 'bearish':
        return '📉';
      case 'neutral':
        return '➡️';
      default:
        return '❓';
    }
  };

  const getPriceMovementIcon = (direction: string) => {
    switch (direction?.toLowerCase()) {
      case 'up':
        return '↗️';
      case 'down':
        return '↘️';
      case 'sideways':
        return '➡️';
      default:
        return '❓';
    }
  };

  const getMagnitudeColor = (magnitude: string) => {
    switch (magnitude?.toLowerCase()) {
      case 'high':
        return 'text-red-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const filteredFeeds = feeds.filter((feed) => {
    const matchesSearch =
      feed.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feed.url.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesActive = !showActiveOnly || feed.isActive;
    return matchesSearch && matchesActive;
  });

  const popularFeeds = feedsService.getPopularFeedUrls();

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <TierPageBanner
        title="Research feeds"
        description="Macro and sector headlines inform Tier 1 sleeves; shorter-horizon feeds align with options tiers (T4–T6)."
        thesisTimeframe="1_month"
      />
      <h1 className="text-3xl font-bold mb-6">RSS Feeds Management</h1>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600 mb-1">Total Feeds</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600 mb-1">Active</p>
            <p className="text-2xl font-bold text-green-600">{stats.active}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600 mb-1">Inactive</p>
            <p className="text-2xl font-bold text-red-600">{stats.inactive}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600 mb-1">Recently Fetched</p>
            <p className="text-2xl font-bold text-blue-600">
              {stats.recentlyFetched}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600 mb-1">Never Fetched</p>
            <p className="text-2xl font-bold text-yellow-600">
              {stats.neverFetched}
            </p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3 items-center mb-6">
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
          onClick={openCreateDialog}
        >
          <Plus className="h-4 w-4" />
          Add Feed
        </button>
        <button
          className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-50"
          onClick={() => setOpenBulkDialog(true)}
        >
          <Plus className="h-4 w-4" />
          Bulk Add
        </button>
        <button
          className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-50 disabled:opacity-50"
          onClick={handleFetchAllActive}
          disabled={fetchingAllActive}
        >
          {fetchingAllActive ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          {fetchingAllActive ? 'Fetching...' : 'Fetch All Active'}
        </button>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search feeds..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={showActiveOnly}
            onChange={(e) => setShowActiveOnly(e.target.checked)}
            className="rounded"
          />
          <span className="text-sm">Active Only</span>
        </label>
      </div>

      {/* Popular Feeds */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Popular RSS Feeds</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(popularFeeds).map(([category, urls]) => (
              <div key={category}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium">{category}</h3>
                  <button
                    onClick={() => toggleCategory(category)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        expandedCategories.includes(category)
                          ? 'rotate-180'
                          : ''
                      }`}
                    />
                  </button>
                </div>
                {expandedCategories.includes(category) && (
                  <div className="space-y-2">
                    {urls.map((url, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {new URL(url).hostname}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {url}
                          </p>
                        </div>
                        <button
                          className="ml-2 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                          onClick={() => {
                            setFormData({
                              name: `${category} - ${new URL(url).hostname}`,
                              url,
                              description: `Auto-added from ${category}`,
                              isActive: true,
                            });
                            setOpenDialog(true);
                          }}
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Feeds Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Articles
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  URL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Fetched
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Items
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredFeeds.map((feed) => {
                const formattedFeed = feedsService.formatFeedData(feed);
                const isExpanded = expandedFeeds.includes(feed._id);
                const feedItems = feedArticles[feed._id] || [];
                const isLoadingArticles = loadingArticles.includes(feed._id);

                return (
                  <React.Fragment key={feed._id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        {(feed.articleCount || 0) > 0 && (
                          <button
                            onClick={() => toggleFeedExpansion(feed._id)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            {isExpanded ? 'Hide' : 'View'} Articles
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {feed.name}
                          </p>
                          {feed.description && (
                            <p className="text-xs text-gray-500">
                              {feed.description}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-900 break-all">
                          {feed.url}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            formattedFeed.statusColor === 'green'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {formattedFeed.statusText}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-900">
                            {formattedFeed.lastFetchedFormatted}
                          </span>
                          {feed.lastFetchedAt && (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900">
                          {feed.articleCount || 0} articles
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleFetchFeed(feed._id)}
                            disabled={fetchingFeeds.includes(feed._id)}
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                          >
                            {fetchingFeeds.includes(feed._id) ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => openEditDialog(feed)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteFeed(feed._id)}
                            className="p-1 text-red-400 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={feed.isActive}
                              onChange={() => handleToggleStatus(feed)}
                              className="rounded"
                            />
                          </label>
                        </div>
                      </td>
                    </tr>
                    {/* Expanded feed items */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={7} className="px-6 py-4 bg-gray-50">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="text-sm font-medium text-gray-900">
                                Recent Articles ({feedItems.length})
                              </h4>
                              <span className="text-xs text-gray-500">
                                Sorted by date (newest first)
                              </span>
                            </div>
                            {isLoadingArticles ? (
                              <div className="text-center py-4">
                                <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                                <p className="text-sm text-gray-500">
                                  Loading articles...
                                </p>
                              </div>
                            ) : feedItems.length > 0 ? (
                              <div className="space-y-2 max-h-96 overflow-y-auto">
                                {feedItems.map((item: any, index: number) => {
                                  const formattedItem = formatFeedItem(item);
                                  const formattedDate = formattedItem.pubDate
                                    ? new Date(
                                        formattedItem.pubDate,
                                      ).toLocaleString()
                                    : 'No date';

                                  // Extract AI analysis data
                                  const tickerSymbols =
                                    item.tickerSymbols || [];
                                  const affectedSectors =
                                    item.affectedSectors || [];
                                  const marketSentiment = item.marketSentiment;
                                  const sentimentConfidence =
                                    item.sentimentConfidence;
                                  const expectedPriceMovement =
                                    item.expectedPriceMovement;
                                  const priceMovementMagnitude =
                                    item.priceMovementMagnitude;
                                  const priceMovementTimeframe =
                                    item.priceMovementTimeframe;
                                  const keyDrivers = item.keyDrivers || [];
                                  const hasAnalysis = item.analyzedAt;

                                  return (
                                    <div
                                      key={index}
                                      className="bg-white rounded-lg border p-3 hover:bg-gray-50"
                                    >
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                          <h5 className="text-sm font-medium text-gray-900 mb-1 line-clamp-2">
                                            {formattedItem.title}
                                          </h5>
                                          {formattedItem.description && (
                                            <p className="text-xs text-gray-600 mb-2 line-clamp-3">
                                              {formattedItem.description}
                                            </p>
                                          )}

                                          {/* AI Analysis Section */}
                                          {hasAnalysis ? (
                                            <div className="mb-2 p-2 bg-blue-50 rounded border border-blue-200">
                                              <div className="flex items-center gap-2 mb-2">
                                                <span className="text-xs font-medium text-blue-800">
                                                  AI Analysis
                                                </span>
                                                {marketSentiment && (
                                                  <span
                                                    className={`text-xs px-2 py-1 rounded-full border ${getSentimentColor(marketSentiment)}`}
                                                  >
                                                    {getSentimentIcon(
                                                      marketSentiment,
                                                    )}{' '}
                                                    {marketSentiment}
                                                    {sentimentConfidence &&
                                                      ` (${sentimentConfidence}%)`}
                                                  </span>
                                                )}
                                              </div>
                                              {/* Tickers and Sectors */}
                                              <div className="flex flex-wrap gap-1 mb-2">
                                                {tickerSymbols.length > 0 && (
                                                  <div className="flex items-center gap-1">
                                                    <span className="text-xs text-gray-600">
                                                      📊
                                                    </span>
                                                    {tickerSymbols
                                                      .slice(0, 3)
                                                      .map(
                                                        (
                                                          ticker: string,
                                                          i: number,
                                                        ) => (
                                                          <span
                                                            key={i}
                                                            className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded"
                                                          >
                                                            {ticker}
                                                          </span>
                                                        ),
                                                      )}
                                                    {tickerSymbols.length >
                                                      3 && (
                                                      <span className="text-xs text-gray-500">
                                                        +
                                                        {tickerSymbols.length -
                                                          3}
                                                      </span>
                                                    )}
                                                  </div>
                                                )}
                                                {affectedSectors.length > 0 && (
                                                  <div className="flex items-center gap-1">
                                                    <span className="text-xs text-gray-600">
                                                      🏢
                                                    </span>
                                                    {affectedSectors
                                                      .slice(0, 2)
                                                      .map(
                                                        (
                                                          sector: string,
                                                          i: number,
                                                        ) => (
                                                          <span
                                                            key={i}
                                                            className="text-xs bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded"
                                                          >
                                                            {sector}
                                                          </span>
                                                        ),
                                                      )}
                                                    {affectedSectors.length >
                                                      2 && (
                                                      <span className="text-xs text-gray-500">
                                                        +
                                                        {affectedSectors.length -
                                                          2}
                                                      </span>
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                              {/* Price Movement Prediction */}
                                              {expectedPriceMovement && (
                                                <div className="flex items-center gap-2 mb-1">
                                                  <span className="text-xs text-gray-600">
                                                    Price:
                                                  </span>
                                                  <span className="text-xs font-medium">
                                                    {getPriceMovementIcon(
                                                      expectedPriceMovement,
                                                    )}{' '}
                                                    {expectedPriceMovement}
                                                  </span>
                                                  {priceMovementMagnitude && (
                                                    <span
                                                      className={`text-xs ${getMagnitudeColor(priceMovementMagnitude)}`}
                                                    >
                                                      ({priceMovementMagnitude})
                                                    </span>
                                                  )}
                                                  {priceMovementTimeframe && (
                                                    <span className="text-xs text-gray-500">
                                                      {priceMovementTimeframe.replace(
                                                        '_',
                                                        ' ',
                                                      )}
                                                    </span>
                                                  )}
                                                </div>
                                              )}
                                              {/* Key Drivers */}
                                              {keyDrivers.length > 0 && (
                                                <div className="flex items-start gap-1">
                                                  <span className="text-xs text-gray-600 mt-0.5">
                                                    🎯
                                                  </span>
                                                  <div className="flex flex-wrap gap-1">
                                                    {keyDrivers
                                                      .slice(0, 2)
                                                      .map(
                                                        (
                                                          driver: string,
                                                          i: number,
                                                        ) => (
                                                          <span
                                                            key={i}
                                                            className="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded"
                                                          >
                                                            {driver}
                                                          </span>
                                                        ),
                                                      )}
                                                    {keyDrivers.length > 2 && (
                                                      <span className="text-xs text-gray-500">
                                                        +{keyDrivers.length - 2}
                                                      </span>
                                                    )}
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          ) : (
                                            <div className="mb-2 p-2 bg-gray-50 rounded border border-gray-200">
                                              <div className="flex items-center justify-between">
                                                <span className="text-xs text-gray-600">
                                                  No AI analysis available
                                                </span>
                                                <button
                                                  onClick={() =>
                                                    handleAnalyzeArticle(
                                                      item._id,
                                                    )
                                                  }
                                                  disabled={analyzingArticles.includes(
                                                    item._id,
                                                  )}
                                                  className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                                                >
                                                  {analyzingArticles.includes(
                                                    item._id,
                                                  ) ? (
                                                    <>
                                                      <Loader2 className="h-3 w-3 animate-spin" />
                                                      Analyzing...
                                                    </>
                                                  ) : (
                                                    <>🤖 Analyze</>
                                                  )}
                                                </button>
                                              </div>
                                            </div>
                                          )}

                                          <div className="flex items-center gap-4 text-xs text-gray-500">
                                            <div className="flex items-center gap-1">
                                              <Calendar className="h-3 w-3" />
                                              <span>{formattedDate}</span>
                                            </div>
                                            {formattedItem.author && (
                                              <div className="flex items-center gap-1">
                                                <User className="h-3 w-3" />
                                                <span>
                                                  {formattedItem.author}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        {formattedItem.link && (
                                          <a
                                            href={formattedItem.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="ml-3 p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
                                            title="Open in new tab"
                                          >
                                            <ExternalLink className="h-4 w-4" />
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-center py-4 text-gray-500">
                                <Rss className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                                <p className="text-sm">No articles available</p>
                                <p className="text-xs">
                                  {fetchingFeeds.includes(feed._id)
                                    ? 'Fetching articles...'
                                    : 'Try fetching the feed to load articles'}
                                </p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      {openDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">
                {editingFeed ? 'Edit Feed' : 'Add New Feed'}
              </h2>
              <button
                onClick={() => setOpenDialog(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL *
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) =>
                    setFormData({ ...formData, url: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://example.com/feed.xml"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                />
              </div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Active</span>
              </label>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setOpenDialog(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={editingFeed ? handleUpdateFeed : handleCreateFeed}
                disabled={!formData.name || !formData.url}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {editingFeed ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Create Dialog */}
      {openBulkDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Bulk Add Feeds</h2>
              <button
                onClick={() => setOpenBulkDialog(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Enter one RSS feed URL per line:
            </p>
            <textarea
              value={bulkUrls}
              onChange={(e) => setBulkUrls(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={10}
              placeholder="https://feeds.reuters.com/reuters/businessNews&#10;https://feeds.bloomberg.com/markets/news.rss&#10;https://www.cnbc.com/id/100003114/device/rss/rss.html"
            />
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setOpenBulkDialog(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkCreate}
                disabled={!bulkUrls.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Create Feeds
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feeds;
